import {
  CategoryEntity,
  categoryIdToCategoryValue,
  DocumentType,
  EntityRefUtil,
  formatDateAsUTC,
  formatDateWithUserTimezone,
  GetDocumentsRequestDTO,
  GetDocumentsResponseDTO,
} from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { startCase } from 'lodash';
import { Link } from 'react-router-dom';

import ExclamationIcon from '@/components/icons/exclamation';
import WarningTriangleIcon from '@/components/icons/warning-triangle';
import { ASSET_SECTION_LABEL } from '@/components/table-page/columns/asset-tags-column';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { getFileTypeIcon } from '@/utils/file-type';
import { getWorkspaceRoute } from '@/utils/routes';

const columnHelper = createColumnHelper<GetDocumentsResponseDTO['data'][number]>();

export const createDocumentsTableColumns = () => {
  const userTimezone = useUserStore.getState().user?.timezone;
  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2">
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label="Select all"
            />
          </label>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
          </label>
        </div>
      ),
      meta: {
        cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
      },
      enableSorting: false,
      enableHiding: false,
    }),
    columnHelper.accessor('name', {
      id: 'name',
      header: 'Document',
      cell: ({ getValue, row }) => {
        const name = getValue();
        if (!name) return <span className="text-secondary-text">-</span>;

        return (
          <a
            className="text-default-blue flex w-full items-center gap-1 underline"
            href={row.original.presignedDownloadUrl || row.original.url}
            target="_blank"
            rel="noopener noreferrer">
            {getFileTypeIcon(row.original.mimeType ?? '')}
            <span className="truncate">{name}</span>
          </a>
        );
      },
      meta: {
        cellClassName: 'max-w-[300px]',
      },
    }),
    columnHelper.accessor('comments', {
      id: 'comments',
      header: 'Comments',
      cell: ({ getValue }) => {
        const comments = getValue();
        if (!comments) return <span className="text-secondary-text">-</span>;

        return (
          <div className="flex w-full items-center gap-1">
            <span className="truncate">{comments}</span>
          </div>
        );
      },
      meta: {
        cellClassName: 'max-w-[300px]',
      },
    }),
    columnHelper.accessor('type', {
      id: 'type',
      header: 'Document Category',
      cell: ({ getValue, row }) => {
        const value = getValue();
        const category = row.original.category;

        return <span>{category ? category.name : startCase(value !== null ? DocumentType[value] : '')}</span>;
      },
      enableColumnFilter: true,
      meta: {
        filterType: 'multi-select',
        // Document category is always set (type or categoryId) — "-" filter is not applicable.
        disableNullFilter: true,
        getFilterOptionsFn: async () => {
          // Every DocumentType value (the 4 system types and the 6 legacy
          // default ones) now always has a permanent real CategoryTable row,
          // so the live category list alone covers every filterable option —
          // listing raw types here too just shows every legacy name twice.
          const { data: categories } = await (await ApiService.GetCategories({ filters: { fields: { entity: CategoryEntity.Document } } })).json();

          return categories.map((category) => ({
            label: category.name,
            value: categoryIdToCategoryValue(category.id),
          }));
        },
      },
    }),
    columnHelper.accessor('expiry', {
      id: 'expiry',
      header: 'Expiry Date',
      cell: ({ getValue }) => {
        const value = getValue();
        const warningThresholdDays = 30;

        if (!value) return <span className="text-secondary-text">-</span>;

        const expiryDate = dayjs(value).startOf('day');
        const today = dayjs().startOf('day');
        const warningThresholdFromNow = dayjs().add(warningThresholdDays, 'day').startOf('day');

        let icon = null;
        if (expiryDate.isBefore(today)) {
          icon = <ExclamationIcon className="text-default-red" />;
        } else if (expiryDate.isBefore(warningThresholdFromNow) || expiryDate.isSame(warningThresholdFromNow)) {
          icon = <WarningTriangleIcon className="text-default-orange" />;
        }

        return (
          <span className="flex items-center gap-1">
            {icon} {formatDateAsUTC(new Date(value), userTimezone)}
          </span>
        );
      },
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: 'Created Date',
      cell: ({ getValue }) => {
        const value = getValue();

        return value ? <span>{formatDateWithUserTimezone(new Date(value), userTimezone)}</span> : <span className="text-secondary-text">-</span>;
      },
    }),
    columnHelper.accessor('asset', {
      id: 'asset',
      header: 'Asset',
      cell: ({ getValue }) => {
        const value = getValue();

        return value?.id ? (
          <Link className="text-default-blue flex w-full items-center gap-1 underline" to={getWorkspaceRoute(`/assets/${value.id}/documents`)}>
            <span className="truncate">{EntityRefUtil.createAssetLabel(value)}</span>
          </Link>
        ) : (
          <span className="text-secondary-text">-</span>
        );
      },
      enableSorting: false,
      meta: { columnGroup: ASSET_SECTION_LABEL },
    }),
    columnHelper.accessor((row) => row.user, {
      id: 'createdBy',
      header: 'Created By',
      cell: ({ row }) => {
        const issue = row.original;
        const createdBy = issue.user;
        const fullName = EntityRefUtil.createUserFullNameRef(createdBy);
        const value = fullName || '-';
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(createdBy?.firstName || '');

        const createdByFullInitials = initials(fullName);
        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={createdByFullInitials}
            avatarUrl={createdBy?.avatarUrl || ''}
            avatarBackgroundColorClass={avatarBackgroundColorClass}
          />
        );
      },
      getGroupingValue: (row) => {
        return EntityRefUtil.createUserFullNameRef(row.user, 'None');
      },
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data: userData } = await (await ApiService.GetUsers({ limit: 100, ...filters })).json();
          const { data: sitemateUserData } = await (
            await ApiService.GetUsers({ limit: 100, filters: { ...filters?.filters, userType: 'sitemate-users' } })
          ).json();

          const resultsMap = new Map();
          userData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });
          sitemateUserData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingUsers } = await (
                await ApiService.GetUsers({
                  limit: 100,
                  filters: { id: missingIds },
                })
              ).json();

              const { data: missingSitemateUsers } = await (
                await ApiService.GetUsers({
                  limit: 100,
                  filters: { id: missingIds, userType: 'sitemate-users' },
                })
              ).json();

              for (const user of missingUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }

              for (const user of missingSitemateUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
  ] as AccessorKeyColumnDef<GetDocumentsResponseDTO['data'][number]>[];
};

export const FILTER_FIELD_MAP: Record<
  NonNullable<ReturnType<typeof createDocumentsTableColumns>[number]['id'] | ReturnType<typeof createDocumentsTableColumns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetDocumentsRequestDTO['filters']>['fields']>
> = {
  name: 'name',
  type: 'type',
  hasExpiry: 'hasExpiry',
  createdBy: 'createdById',
};
